class DOMManager {
  private static instance: DOMManager
  private managedElements: Map<string, HTMLElement> = new Map()
  private isCleaningUp: boolean = false

  private constructor() {}

  static getInstance(): DOMManager {
    if (!DOMManager.instance) {
      DOMManager.instance = new DOMManager()
    }
    return DOMManager.instance
  }

  addElement(key: string, element: HTMLElement): void {
    if (this.isCleaningUp) return

    // Remove existing element with same key
    this.removeElement(key)

    try {
      // Ensure document.head exists and element is valid before trying to append
      if (document.head && element && !element.isConnected) {
        // Add a custom property to track that this element is managed by us
        ;(element as any).__domManagerManaged = true

        // Override removeChild method on the parent to prevent errors
        const originalRemoveChild = document.head.removeChild
        if (!(document.head as any).__removeChildPatched) {
          document.head.removeChild = function (child: Node) {
            try {
              if (this.contains(child)) {
                return originalRemoveChild.call(this, child)
              }
              return child as any
            } catch {
              return child as any
            }
          }
          ;(document.head as any).__removeChildPatched = true
        }

        document.head.appendChild(element)
        this.managedElements.set(key, element)
      }
    } catch (error) {
      console.warn(`Failed to add element with key ${key}:`, error)
    }
  }

  removeElement(key: string): void {
    const element = this.managedElements.get(key)
    if (element) {
      try {
        // Check if element is still connected to DOM and if head exists
        if (element.isConnected && document.head && document.head.contains(element)) {
          // Mark element for removal to prevent conflicts
          ;(element as any).__domManagerRemoving = true
          element.remove() // Use modern remove() method instead of removeChild()
        }
      } catch {
        // Silently handle removal errors - element might already be removed
      } finally {
        // Always remove from our tracking map
        this.managedElements.delete(key)
      }
    }
  }

  removeElementsByPrefix(prefix: string): void {
    const keysToRemove = Array.from(this.managedElements.keys()).filter((key) => key.startsWith(prefix))
    keysToRemove.forEach((key) => this.removeElement(key))
  }

  cleanup(): void {
    this.isCleaningUp = true
    this.managedElements.forEach((element) => {
      try {
        if (element.isConnected && document.head && document.head.contains(element)) {
          // Mark element for removal to prevent conflicts
          ;(element as any).__domManagerRemoving = true
          element.remove() // Use modern remove() method
        }
      } catch {
        // Silently handle cleanup errors
      }
    })
    this.managedElements.clear()
    this.isCleaningUp = false
  }
}

export default DOMManager
